import React, { useMemo, useRef, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả"
        />
    );
};

const PublicationBookForm = ({
    isOpen,
    onClose,
    onSubmit,
    formData,
    setFormData,
    isEditing,
    users = [],
    languages = [],
    countries = [],
    projectTypes = [],
    educationLevels = [],
    members = [],
    setMembers,
    toast
}) => {

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return {
                label: displayLabel,
                value: u.Id,
                searchKey: searchStr,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const countryOptions = useMemo(() => {
        return countries.map(c => ({
            label: c.Name,
            value: c.Name
        }));
    }, [countries]);

    const filteredEducationLevels = useMemo(() => {
        const targetNames = ["Đại học", "Sau đại học"];
        const found = educationLevels.filter(el => targetNames.includes(el.Name));
        if (found.length === 2) return found;
        return targetNames.map((name, idx) => ({ Id: idx + 1, Name: name }));
    }, [educationLevels]);

    const parsedFiles = useMemo(() => {
        if (!formData.AttachmentUrl) return [];
        try {
            if (formData.AttachmentUrl.startsWith('[')) {
                return JSON.parse(formData.AttachmentUrl);
            }
            if (formData.AttachmentUrl.includes('|')) {
                const parts = formData.AttachmentUrl.split('|');
                return [{ name: parts[0], data: parts[1], description: '' }];
            }
        } catch (e) {
            return [];
        }
        return [];
    }, [formData.AttachmentUrl]);

    const currentUserStr = localStorage.getItem('user');
    const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
    const currentUserId = currentUserInfo ? Number(currentUserInfo.Id) : 0;

    const isReadOnlyForm = useMemo(() => {
        const isLockedStatus = formData.Status === 'Đã gửi duyệt' || formData.Status === 'Đã duyệt';
        const isNotOwner = formData.UserId && Number(formData.UserId) !== currentUserId;
        return isLockedStatus || isNotOwner;
    }, [formData.Status, formData.UserId, currentUserId]);

    useEffect(() => {
        if (isOpen && members.length > 0) {
            const updatedMembers = members.map(m => {
                if (m.userId || m.UserId) {
                    const user = users.find(u => u.Id === parseInt(m.userId || m.UserId || m.Id));
                    if (user) {
                        return {
                            ...m,
                            userId: user.Id,
                            UserId: user.Id,
                            fullName: `${user.LastName || ''} ${user.FirstName || ''}`.trim(),
                            FullName: `${user.LastName || ''} ${user.FirstName || ''}`.trim(),
                            department: user.DepartmentName || m.department || m.DepartmentName || ''
                        };
                    }
                }
                return m;
            });

            if (JSON.stringify(members) !== JSON.stringify(updatedMembers)) {
                setMembers(updatedMembers);
            }
        }
    }, [isOpen, users, members, setMembers]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        if (isReadOnlyForm) return;
        const { name, value, type } = e.target;
        setFormData({ ...formData, [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value });
    };

    const handleDateChange = (name, dateObj) => {
        if (isReadOnlyForm) return;
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString, type: 'text' } });
    };

    const handleTitleBlur = async (e) => {
        if (isReadOnlyForm) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 5) return;

        try {
            const roleNameInfo = (currentUserInfo?.RoleName || '').toLowerCase();
            const isAdminMode = currentUserInfo?.RoleId === 1 || currentUserInfo?.RoleId === 0 || roleNameInfo === 'admin' ||
                (roleNameInfo.includes('trưởng khoa') && currentUserInfo?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/publicationbook?userId=${currentUserId}&isAdmin=${isAdminMode}`, {
                headers: {
                    'X-User-Role-Id': currentUserInfo?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUserInfo?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.PublishDate ? formData.PublishDate.substring(0, 4) : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.BookName);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.PublishDate ? item.PublishDate.substring(0, 4) : '';

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear || (formData.ISBN && item.ISBN === formData.ISBN)) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện Sách/Giáo trình rất giống đã tồn tại: "${item.BookName}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp sách:", err);
        }
    };

    const updateEvidenceFiles = (newFiles) => {
        if (isReadOnlyForm) return;
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(newFiles) });
    };

    const handleMultiFileChange = (e) => {
        if (isReadOnlyForm) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const allSelectedFiles = Array.from(e.target.files);
        if (allSelectedFiles.length === 0) return;

        const validFiles = allSelectedFiles.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Định dạng không hợp lệ',
                    detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`,
                    life: 5000
                });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Dung lượng quá lớn',
                    detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`,
                    life: 5000
                });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesPromises = validFiles.map(file => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve({ name: file.name, data: reader.result, description: '' });
            reader.readAsDataURL(file);
        }));

        Promise.all(newFilesPromises).then(newFiles => {
            updateEvidenceFiles([...parsedFiles, ...newFiles]);
        });
        e.target.value = null;
    };

    const handleFileDescChange = (index, desc) => {
        if (isReadOnlyForm) return;
        const updated = [...parsedFiles];
        updated[index].description = desc;
        updateEvidenceFiles(updated);
    };

    const removeEvidenceFile = (index) => {
        if (isReadOnlyForm) return;
        const updated = parsedFiles.filter((_, i) => i !== index);
        updateEvidenceFiles(updated);
    };

    const downloadIndividualFile = async (fileObj) => {
        let fileUrl = fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL;
        if (!fileUrl) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!', life: 3000 });
            return;
        }

        const base64ToBlob = (dataUrl) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime });
        };

        if (typeof fileUrl === 'string') {
            fileUrl = fileUrl.trim();
            if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
            }
        }

        try {
            if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                const blob = base64ToBlob(fileUrl);
                const objectUrl = URL.createObjectURL(blob);

                const newWindow = window.open(objectUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            }

            const newWindow = window.open(fileUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Lỗi khi xử lý mở file:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
        }
    };

    const handleAddMember = () => {
        if (isReadOnlyForm) return;
        const newMembers = [...members, { userId: '', UserId: '', fullName: '', FullName: '', role: '', department: '', isStudent: false, IsStudent: false, isInstructor: false, isExternal: false }];
        setMembers(newMembers);
    };

    const handleRemoveMember = (index) => {
        if (isReadOnlyForm) return;
        const newMembers = members.filter((_, i) => i !== index);
        setMembers(newMembers);
    };

    const handleMemberSelect = (index, userId) => {
        if (isReadOnlyForm) return;
        const selectedUser = users.find(u => u.Id === parseInt(userId));
        const newMembers = [...members];
        if (selectedUser) {
            newMembers[index] = {
                ...newMembers[index],
                userId: selectedUser.Id,
                UserId: selectedUser.Id,
                fullName: `${selectedUser.LastName} ${selectedUser.FirstName}`,
                FullName: `${selectedUser.LastName} ${selectedUser.FirstName}`,
                department: selectedUser.DepartmentName,
                isExternal: false,
                isStudent: false,
                IsStudent: false
            };
        } else {
            newMembers[index] = { userId: '', UserId: '', fullName: '', FullName: '', role: '', department: '', isStudent: false, IsStudent: false, isInstructor: false, isExternal: false };
        }
        setMembers(newMembers);
    };

    const handleRoleChange = (index, newRole) => {
        if (isReadOnlyForm) return;
        const newMembers = [...members];
        newMembers[index].role = newRole;
        newMembers[index].Role = newRole;
        setMembers(newMembers);
    };

    const handleMemberFieldChange = (index, field, value) => {
        if (isReadOnlyForm) return;
        const newMembers = [...members];

        newMembers[index][field] = value;
        if (field === 'isInstructor' || field === 'IsInstructor') {
            newMembers[index].isInstructor = value;
            newMembers[index].IsInstructor = value;
        }

        setMembers(newMembers);
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box form-modal-box" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                        {isReadOnlyForm ? "Xem chi tiết Sách - Giáo trình" : (isEditing ? "Cập nhật Sách - Giáo trình" : "Thêm mới Sách - Giáo trình")}
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="bookForm" onSubmit={(e) => { if (isReadOnlyForm) e.preventDefault(); else onSubmit(e); }}>
                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tên công trình *</label>
                            <input
                                type="text"
                                name="BookName"
                                className="form-input"
                                value={formData.BookName || ''}
                                onChange={handleChange}
                                onBlur={handleTitleBlur}
                                required
                                disabled={isReadOnlyForm}
                            />
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nhà xuất bản</label>
                                <input type="text" name="Publisher" className="form-input" value={formData.Publisher || ''} onChange={handleChange} disabled={isReadOnlyForm} />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày xuất bản *</label>
                                <Calendar
                                    id="PublishDate"
                                    name="PublishDate"
                                    value={(formData.PublishDate && !isNaN(new Date(formData.PublishDate).getTime())) ? new Date(formData.PublishDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('PublishDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnlyForm}
                                    required
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                            <div className="form-group" style={{ flex: '1.5 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nơi xuất bản</label>
                                <input type="text" name="PublishPlace" className="form-input" value={formData.PublishPlace || ''} onChange={handleChange} disabled={isReadOnlyForm} />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>ISSN/ISBN</label>
                                <input type="text" name="ISBN" className="form-input" value={formData.ISBN || ''} onChange={handleChange} disabled={isReadOnlyForm} />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngôn ngữ</label>
                                <select name="Language" className="form-input" value={formData.Language || ''} onChange={handleChange} disabled={isReadOnlyForm}>
                                    <option value="">Chọn ngôn ngữ</option>
                                    {languages.map(l => <option key={l.Id} value={l.Name}>{l.Name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Loại công trình *</label>
                                <select name="ProjectType" className="form-input" value={formData.ProjectType || ''} onChange={handleChange} required disabled={isReadOnlyForm}>
                                    <option value="">Chọn loại công trình</option>
                                    {projectTypes.map(pt => <option key={pt.Id} value={pt.Name}>{pt.Name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Quốc gia</label>
                                <SearchableFocusDropdown
                                    value={formData.Country || ''}
                                    options={countryOptions}
                                    onChange={(e) => handleChange({ target: { name: 'Country', value: e.value } })}
                                    placeholder="Chọn quốc gia"
                                    className="form-input"
                                    panelUniqueClass="custom-dropdown-panel-pub-book-country"
                                    disabled={isReadOnlyForm}
                                    itemTemplate={userItemTemplate}
                                />
                            </div>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Trình độ *</label>
                                <select name="EducationLevel" className="form-input" value={formData.EducationLevel || ''} onChange={handleChange} required disabled={isReadOnlyForm}>
                                    <option value="">Chọn trình độ</option>
                                    {filteredEducationLevels.map(el => (
                                        <option key={el.Id} value={el.Name}>{el.Name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số xuất bản</label>
                                <input type="text" name="PublishNumber" className="form-input" value={formData.PublishNumber || ''} onChange={handleChange} disabled={isReadOnlyForm} />
                            </div>
                        </div>

                        <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px', border: '1px solid #eee' }}>
                            <p style={{ fontWeight: 'bold', color: '#003399', marginBottom: '15px', fontSize: '14px', textTransform: 'uppercase' }}>Thông tin tác giả</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 120px' }}>
                                    <label style={{ fontSize: '13px' }}>Tổng số tác giả *</label>
                                    <input type="number" min="1" name="TotalAuthors" className="form-input" value={formData.TotalAuthors || ''} onChange={handleChange} required disabled={isReadOnlyForm} />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 120px' }}>
                                    <label style={{ fontSize: '13px' }}>Số tác giả chính *</label>
                                    <input type="number" min="1" name="MainAuthors" className="form-input" value={formData.MainAuthors || ''} onChange={handleChange} required disabled={isReadOnlyForm} />
                                </div>
                                <div className="form-group" style={{ flex: '2 1 250px' }}>
                                    <label style={{ fontSize: '13px' }}>Danh sách tác giả nguyên bản *</label>
                                    <input type="text" name="OriginalAuthors" className="form-input" value={formData.OriginalAuthors || ''} onChange={handleChange} required disabled={isReadOnlyForm} />
                                </div>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                <label style={{ margin: 0, fontWeight: 'bold' }}>Danh sách thành viên THUỘC TRƯỜNG *</label>
                                {!isReadOnlyForm && (
                                    <button
                                        type="button"
                                        onClick={handleAddMember}
                                        style={{
                                            margin: 0,
                                            padding: '6px 14px',
                                            backgroundColor: isReadOnlyForm ? '#bdc3c7' : '#28a745',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: isReadOnlyForm ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }}
                                        disabled={isReadOnlyForm}
                                    >
                                        <i className="fa-solid fa-user-plus"></i> Chọn người dùng hoặc thêm Sinh viên
                                    </button>
                                )}
                            </div>

                            <div style={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                <table className="custom-table" style={{ width: '100%', minWidth: '1200px', margin: 0, border: 'none' }}>
                                    <thead style={{ backgroundColor: '#f9f9f9' }}>
                                        <tr>
                                            <th width="5%" style={{ textAlign: 'center' }}>#</th>
                                            <th width="25%">Họ tên</th>
                                            <th width="12%">Vai trò</th>
                                            <th width="18%">Đơn vị công tác</th>
                                            <th width="10%" style={{ textAlign: 'center' }}>Ngoài trường</th>
                                            <th width="10%" style={{ textAlign: 'center' }}>Là Sinh viên</th>
                                            <th width="15%" style={{ textAlign: 'center' }}>Là GV Hướng dẫn</th>
                                            {!isReadOnlyForm && <th width="5%" style={{ textAlign: 'center' }}>Xóa</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.length === 0 ? (
                                            <tr><td colSpan={isReadOnlyForm ? "7" : "8"} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>Chưa có thành viên nào</td></tr>
                                        ) : members.map((m, i) => {
                                            const isStu = m.isStudent || m.IsStudent || false;
                                            const isExt = m.isExternal || (!m.userId && !m.UserId && (m.fullName || m.FullName) && !isStu);
                                            const isExtOrStudent = isExt || isStu;

                                            return (
                                                <tr key={i}>
                                                    <td style={{ textAlign: 'center' }}>{i + 1}</td>
                                                    <td>
                                                        {isExtOrStudent ? (
                                                            <input
                                                                type="text"
                                                                className="form-input"
                                                                placeholder={isStu ? "Nhập họ tên Sinh viên..." : "Nhập họ tên người ngoài trường..."}
                                                                value={m.fullName || m.FullName || ''}
                                                                onChange={(e) => {
                                                                    const updated = [...members];
                                                                    updated[i].fullName = e.target.value;
                                                                    updated[i].FullName = e.target.value;
                                                                    setMembers(updated);
                                                                }}
                                                                disabled={isReadOnlyForm}
                                                                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                                                            />
                                                        ) : (
                                                            <SearchableFocusDropdown
                                                                value={m.userId || m.UserId}
                                                                options={userOptions}
                                                                onChange={(e) => handleMemberSelect(i, e.value)}
                                                                placeholder="Chọn thành viên"
                                                                panelUniqueClass={`custom-dropdown-panel-pub-book-member-${i}`}
                                                                disabled={isReadOnlyForm}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <select
                                                            className="form-input"
                                                            value={m.role || m.Role || ''}
                                                            onChange={(e) => handleRoleChange(i, e.target.value)}
                                                            disabled={isReadOnlyForm}
                                                            style={{ width: '100%', minWidth: '140px', cursor: isReadOnlyForm ? 'not-allowed' : 'pointer' }}
                                                            required
                                                        >
                                                            <option value="" disabled>Chọn vai trò</option>
                                                            <option value="Chủ biên">Chủ biên</option>
                                                            <option value="Đồng chủ biên">Đồng chủ biên</option>
                                                            <option value="Thành viên">Thành viên</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: '13px', color: '#555' }}>
                                                            {m.department || m.DepartmentName || (isStu ? 'Sinh viên' : (isExt ? 'Ngoài trường' : '---'))}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isExt}
                                                            onChange={(e) => {
                                                                const updated = [...members];
                                                                const checked = e.target.checked;
                                                                updated[i].isExternal = checked;
                                                                if (checked) {
                                                                    updated[i].isStudent = false;
                                                                    updated[i].IsStudent = false;
                                                                    updated[i].userId = '';
                                                                    updated[i].UserId = '';
                                                                    updated[i].department = '';
                                                                    updated[i].isInstructor = false;
                                                                    updated[i].IsInstructor = false;
                                                                } else {
                                                                    updated[i].fullName = '';
                                                                    updated[i].FullName = '';
                                                                }
                                                                setMembers(updated);
                                                            }}
                                                            disabled={isReadOnlyForm}
                                                            style={{ cursor: isReadOnlyForm ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isStu}
                                                            onChange={(e) => {
                                                                const updated = [...members];
                                                                const checked = e.target.checked;
                                                                updated[i].isStudent = checked;
                                                                updated[i].IsStudent = checked;
                                                                if (checked) {
                                                                    updated[i].isExternal = false;
                                                                    updated[i].userId = '';
                                                                    updated[i].UserId = '';
                                                                    updated[i].department = '';
                                                                    updated[i].isInstructor = false;
                                                                    updated[i].IsInstructor = false;
                                                                } else {
                                                                    updated[i].fullName = '';
                                                                    updated[i].FullName = '';
                                                                }
                                                                setMembers(updated);
                                                            }}
                                                            disabled={isReadOnlyForm}
                                                            style={{ cursor: isReadOnlyForm ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={m.isInstructor || m.IsInstructor || false}
                                                            onChange={(e) => handleMemberFieldChange(i, 'isInstructor', e.target.checked)}
                                                            disabled={isReadOnlyForm || isStu}
                                                            style={{ cursor: (isReadOnlyForm || isStu) ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                                                        />
                                                    </td>
                                                    {!isReadOnlyForm && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <i className="fa-solid fa-xmark" style={{ color: '#e74c3c', cursor: 'pointer', fontSize: '16px' }} onClick={() => handleRemoveMember(i)} title="Xóa thành viên này"></i>
                                                        </td>
                                                    )}
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Tệp tin minh chứng</label>

                            <p style={{ color: '#e74c3c', fontStyle: 'italic', marginBottom: '15px', marginTop: '-5px', fontSize: '14px' }}>
                                * Chú ý: Hệ thống chỉ nhận tệp minh chứng ở định dạng PDF, JPG/JPEG và PNG. Dung lượng tối đa 5MB / tệp
                            </p>

                            {parsedFiles.length > 0 && (
                                <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                                    <table className="custom-table" style={{ width: '100%', minWidth: '500px', marginBottom: 0, border: '1px solid #eee' }}>
                                        <thead style={{ backgroundColor: '#f9f9f9' }}>
                                            <tr>
                                                <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                <th>Mô tả</th>
                                                <th width="35%">Minh chứng</th>
                                                {!isReadOnlyForm && <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsedFiles.map((file, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                    <td>
                                                        <input type="text" className="form-input" placeholder="Nhập thông tin mô tả" value={file.description || ''} onChange={(e) => handleFileDescChange(idx, e.target.value)} style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }} disabled={isReadOnlyForm} />
                                                    </td>
                                                    <td>
                                                        <span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để tải về">{file.name}</span>
                                                    </td>
                                                    {!isReadOnlyForm && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => removeEvidenceFile(idx)} style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!isReadOnlyForm && (
                                <div style={{ border: '2px dashed #3498db', borderRadius: '8px', padding: '30px', textAlign: 'center', backgroundColor: '#ebf5fb', position: 'relative', overflow: 'hidden' }}>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
                                        onChange={handleMultiFileChange}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                        title="Nhấn để chọn tệp"
                                    />
                                    {parsedFiles.length > 0 ? (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '35px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Đã có {parsedFiles.length} tệp minh chứng</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Thêm tệp tin khác</button>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Chưa có tệp nào được chọn</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Chọn tệp từ thiết bị</button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </form>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    {isReadOnlyForm ? (
                        <button type="button" className="btn-submit" onClick={onClose}>Đóng</button>
                    ) : (
                        <>
                            <button type="button" className="btn-cancel" onClick={onClose}>Hủy</button>
                            <button type="submit" form="bookForm" className="btn-submit">Hoàn tất</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicationBookForm;